import React, { useMemo, useState } from 'react'

import { Button, Checkbox, Dropdown, Input, Select } from 'antd'

import { SearchOutlined } from '@ant-design/icons'
import { countBy } from 'lodash'

type Props = {
  rowMetaTags: string[]
  value: string[]
  onChange: (value: string[]) => void
}

const DataTypeSelect = ({ rowMetaTags, value, onChange }: Props) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rowCounts = useMemo(() => countBy(rowMetaTags), [rowMetaTags])
  const types = useMemo(() => Object.keys(rowCounts), [rowCounts])
  const search = query.trim().toLowerCase()
  const visibleTypes = types.filter(type => type.toLowerCase().includes(search))
  const selected = value.filter(type => types.includes(type))

  const toggle = (type: string, checked: boolean) => {
    onChange(checked ? [...value, type] : value.filter(x => x !== type))
  }

  const panel = (
    <div className="data-type-panel">
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search data types"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      <div className="data-type-actions">
        <span>
          {selected.length} of {types.length} selected
        </span>
        <span>
          <Button type="link" size="small" onClick={() => onChange(types)}>
            Select all
          </Button>
          <span className="sep">·</span>
          <Button type="link" size="small" onClick={() => onChange([])}>
            Clear
          </Button>
        </span>
      </div>
      <div className="data-type-options">
        {visibleTypes.map(type => (
          <label key={type} className="data-type-option">
            <Checkbox
              checked={selected.includes(type)}
              onChange={e => toggle(type, e.target.checked)}
            />
            <span className="data-type-name">{type}</span>
            <span className="data-type-count">
              {rowCounts[type]} {rowCounts[type] === 1 ? 'row' : 'rows'}
            </span>
          </label>
        ))}
        {visibleTypes.length === 0 && <div className="data-type-empty">No data types found</div>}
      </div>
    </div>
  )

  return (
    <Dropdown
      overlay={panel}
      trigger={['click']}
      visible={open}
      onVisibleChange={visible => {
        setOpen(visible)
        if (!visible) {
          setQuery('')
        }
      }}
    >
      <div className="data-type-select">
        <Select
          mode="multiple"
          open={false}
          showArrow
          showSearch={false}
          maxTagCount="responsive"
          className={open ? 'is-open' : undefined}
          value={selected}
          options={types.map(type => ({ label: type, value: type }))}
          placeholder="Choose data types"
          tagRender={({ label }) => <span className="data-type-chip">{label}</span>}
          maxTagPlaceholder={omitted => (
            <span className="data-type-chip more">+{omitted.length}</span>
          )}
        />
      </div>
    </Dropdown>
  )
}

export default DataTypeSelect
